import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckMemberFamilyMemberDuplicationMutation } from '@/api/member-management/memberDuplication';
import { FamilyMemberDuplicationCheckDTO } from '@/api/DTO/family/family.interface';
import LinkCandidateDialog, {
	LinkCandidateChoice,
} from '@/common/components/identity-link/LinkCandidateDialog';
import LinkConfirmDialog from '@/common/components/identity-link/LinkConfirmDialog';
import { useFamilyDuplicationColumns } from '@/pages/family-management/family-management-summary/components/FamilyMemberDuplicateDialog';

interface Props {
	trigger: number;
	/** Member detail (`AdmissionRequestDetailDTO`). */
	details: any;
	/** Parent's `setOpen`; opens the register form. */
	setLastOpen: (open: boolean) => void;
	/** Family member to link with the register request; `null` = no link. */
	setLinkFamilyMember: (value: FamilyMemberDuplicationCheckDTO | null) => void;
}

/**
 * DMS-REQ-WEB-CHA-005 §四 (passive, member side): before the register form,
 * list admitted family members that may be the same person. A picked record
 * is confirmed (§五) and sent with the register request.
 */
const FamilyMemberLinkCheck = ({
	trigger,
	details,
	setLastOpen,
	setLinkFamilyMember,
}: Props) => {
	const { t } = useTranslation();
	const columns = useFamilyDuplicationColumns();
	const [checkMemberFamilyMemberDuplication] =
		useCheckMemberFamilyMemberDuplicationMutation();

	const [candidates, setCandidates] = useState<
		FamilyMemberDuplicationCheckDTO[]
	>([]);
	const candidateResolver = useRef<
		| ((choice: LinkCandidateChoice<FamilyMemberDuplicationCheckDTO>) => void)
		| undefined
	>(undefined);
	const [linkTarget, setLinkTarget] =
		useState<FamilyMemberDuplicationCheckDTO | null>(null);
	const confirmResolver = useRef<((confirmed: boolean) => void) | undefined>(
		undefined
	);

	const chooseCandidate = (rows: FamilyMemberDuplicationCheckDTO[]) =>
		new Promise<LinkCandidateChoice<FamilyMemberDuplicationCheckDTO>>(
			(resolve) => {
				candidateResolver.current = resolve;
				setCandidates(rows);
			}
		);

	const handleCandidateClose = (
		choice: LinkCandidateChoice<FamilyMemberDuplicationCheckDTO>
	) => {
		setCandidates([]);
		candidateResolver.current?.(choice);
		candidateResolver.current = undefined;
	};

	const confirmLink = (target: FamilyMemberDuplicationCheckDTO) =>
		new Promise<boolean>((resolve) => {
			confirmResolver.current = resolve;
			setLinkTarget(target);
		});

	const handleConfirmClose = (confirmed: boolean) => {
		setLinkTarget(null);
		confirmResolver.current?.(confirmed);
		confirmResolver.current = undefined;
	};

	const check = async () => {
		setLinkFamilyMember(null);
		// Already linked: nothing to ask.
		if (details?.linkedFamilyMemberId) {
			setLastOpen(true);
			return;
		}
		const response = await checkMemberFamilyMemberDuplication({
			userId: details.id,
		});
		if (!('data' in response) || !response.data?.success) return;
		const hits = response.data.data ?? [];
		if (hits.length === 0) {
			setLastOpen(true);
			return;
		}
		// "Back" on the confirmation returns to the candidate list.
		for (;;) {
			const choice = await chooseCandidate(hits);
			if (choice.action === 'cancel') return;
			if (choice.action === 'skip') break;
			if (await confirmLink(choice.record)) {
				setLinkFamilyMember(choice.record);
				break;
			}
		}
		setLastOpen(true);
	};

	useEffect(() => {
		if (trigger) check();
	}, [trigger]);

	return (
		<>
			<LinkCandidateDialog
				open={candidates.length > 0}
				title={t('identityLink.familyMemberCandidateTitle')}
				text={t('identityLink.familyMemberCandidateText')}
				rows={candidates}
				columns={columns}
				onClose={handleCandidateClose}
			/>
			<LinkConfirmDialog
				open={!!linkTarget}
				direction='toFamilyMember'
				targetId={linkTarget?.id}
				targetFileNo={linkTarget?.familyMemberFileNo}
				source={details ?? {}}
				onClose={handleConfirmClose}
			/>
		</>
	);
};

export default FamilyMemberLinkCheck;
